/**
 * Employee Benefits Portal Dashboard
 *
 * Primary landing page for employees showing:
 * - Plan coverages (Medical, Dental, Vision)
 * - Out-of-pocket progress and deductibles
 * - FSA/HSA balances and transactions
 * - Other employer benefits summary
 * - Quick actions
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  DollarSign,
  FileText,
  Shield,
  Users,
  CreditCard,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Activity,
  Gift,
  MessageSquare,
  Briefcase,
  Building2,
  ChevronRight,
  Star,
  Sparkles,
  Calendar,
  Eye,
  Stethoscope,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { formatDateOnly } from '@/utils/dateTime';
import EmployeePortalLayout from '../layout';
import { PortalHero } from '@/components/weavein';

interface EmployeeData {
  id: number;
  employee_number: string;
  first_name: string;
  last_name: string;
  employer_id: number;
  employer_name?: string;
  job_title?: string;
  department?: string;
}

interface PlanCoverage {
  plan_type: 'medical' | 'dental' | 'vision';
  plan_name: string;
  carrier: string;
  coverage_tier: string; // Employee Only, Employee + Spouse, Employee + Family
  status: 'active' | 'pending' | 'terminated';
  effective_date: string;
  copay_pcp?: number;
  copay_specialist?: number;
  deductible_individual?: number;
  deductible_family?: number;
  oop_max_individual?: number;
  oop_max_family?: number;
}

interface BenefitsAccumulator {
  deductible_met: number;
  deductible_total: number;
  oop_met: number;
  oop_max: number;
  plan_type: 'individual' | 'family';
}

interface AccountBalance {
  account_type: 'HSA' | 'FSA' | 'HRA' | 'DCFSA';
  balance: number;
  ytd_contributions: number;
  employer_contributions: number;
  annual_limit: number;
  use_by_date?: string;
}

interface RecentClaim {
  id: number;
  service_date: string;
  provider_name: string;
  service_type: string;
  billed_amount: number;
  allowed_amount: number;
  patient_responsibility: number;
  status: 'processed' | 'pending' | 'denied';
}

export default function EmployeePortalDashboard() {
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [planCoverages, setPlanCoverages] = useState<PlanCoverage[]>([]);
  const [accumulator, setAccumulator] = useState<BenefitsAccumulator | null>(null);
  const [accounts, setAccounts] = useState<AccountBalance[]>([]);
  const [recentClaims, setRecentClaims] = useState<RecentClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const member = getMemberData();

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch employee data
        const employeeUrl = getWeaveinApiUrl(`/api/employees/by-member/${member.id}`);
        const employeeResponse = await fetch(employeeUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        });

        if (employeeResponse.ok) {
          const empData = await employeeResponse.json();
          setEmployeeData(empData);

          // Fetch benefits data (plan coverages, accumulators, accounts, claims)
          // These would come from real API endpoints in production
          try {
            const [coveragesRes, accumulatorRes, accountsRes, claimsRes] = await Promise.all([
              fetch(getWeaveinApiUrl(`/api/employees/${empData.id}/plan-coverages`), {
                headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
              }).catch(() => null),
              fetch(getWeaveinApiUrl(`/api/employees/${empData.id}/benefits-accumulator`), {
                headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
              }).catch(() => null),
              fetch(getWeaveinApiUrl(`/api/employees/${empData.id}/benefit-accounts`), {
                headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
              }).catch(() => null),
              fetch(getWeaveinApiUrl(`/api/employees/${empData.id}/recent-claims`), {
                headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
              }).catch(() => null),
            ]);

            if (coveragesRes?.ok) {
              const coveragesData = await coveragesRes.json();
              setPlanCoverages(coveragesData);
            } else {
              // Use mock data for demo
              setPlanCoverages([
                {
                  plan_type: 'medical',
                  plan_name: 'Gold PPO Plan',
                  carrier: 'Blue Shield',
                  coverage_tier: 'Employee + Family',
                  status: 'active',
                  effective_date: '2025-01-01',
                  copay_pcp: 25,
                  copay_specialist: 50,
                  deductible_individual: 500,
                  deductible_family: 1500,
                  oop_max_individual: 3000,
                  oop_max_family: 6000,
                },
                {
                  plan_type: 'dental',
                  plan_name: 'Delta Dental PPO',
                  carrier: 'Delta Dental',
                  coverage_tier: 'Employee + Family',
                  status: 'active',
                  effective_date: '2025-01-01',
                },
                {
                  plan_type: 'vision',
                  plan_name: 'VSP Choice',
                  carrier: 'VSP',
                  coverage_tier: 'Employee + Family',
                  status: 'active',
                  effective_date: '2025-01-01',
                },
              ]);
            }

            if (accumulatorRes?.ok) {
              const accData = await accumulatorRes.json();
              setAccumulator(accData);
            } else {
              // Mock data
              setAccumulator({
                deductible_met: 320,
                deductible_total: 500,
                oop_met: 1200,
                oop_max: 3000,
                plan_type: 'individual',
              });
            }

            if (accountsRes?.ok) {
              const accountsData = await accountsRes.json();
              setAccounts(accountsData);
            } else {
              // Mock data
              setAccounts([
                {
                  account_type: 'HSA',
                  balance: 2847.50,
                  ytd_contributions: 1800,
                  employer_contributions: 500,
                  annual_limit: 4150,
                },
                {
                  account_type: 'FSA',
                  balance: 1450.00,
                  ytd_contributions: 2850,
                  employer_contributions: 0,
                  annual_limit: 3050,
                  use_by_date: '2026-03-15',
                },
              ]);
            }

            if (claimsRes?.ok) {
              const claimsData = await claimsRes.json();
              setRecentClaims(claimsData);
            } else {
              // Mock data
              setRecentClaims([
                {
                  id: 1,
                  service_date: '2025-11-15',
                  provider_name: 'Dr. Sarah Johnson',
                  service_type: 'Primary Care Visit',
                  billed_amount: 150,
                  allowed_amount: 125,
                  patient_responsibility: 25,
                  status: 'processed',
                },
                {
                  id: 2,
                  service_date: '2025-10-28',
                  provider_name: 'Quest Diagnostics',
                  service_type: 'Lab Work',
                  billed_amount: 280,
                  allowed_amount: 180,
                  patient_responsibility: 45,
                  status: 'processed',
                },
                {
                  id: 3,
                  service_date: '2025-10-15',
                  provider_name: 'CVS Pharmacy',
                  service_type: 'Prescription',
                  billed_amount: 85,
                  allowed_amount: 85,
                  patient_responsibility: 15,
                  status: 'processed',
                },
              ]);
            }
          } catch (err) {
            console.error('Error fetching benefits data:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [member?.id]);

  const medicalPlan = planCoverages.find(p => p.plan_type === 'medical');
  const dentalPlan = planCoverages.find(p => p.plan_type === 'dental');
  const visionPlan = planCoverages.find(p => p.plan_type === 'vision');
  const hsaAccount = accounts.find(a => a.account_type === 'HSA');
  const fsaAccount = accounts.find(a => a.account_type === 'FSA');

  const deductiblePercent = accumulator
    ? Math.min(100, (accumulator.deductible_met / accumulator.deductible_total) * 100)
    : 0;
  const oopPercent = accumulator
    ? Math.min(100, (accumulator.oop_met / accumulator.oop_max) * 100)
    : 0;

  return (
    <EmployeePortalLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Welcome Header */}
        <PortalHero
          variant="vibrant"
          title={<>Welcome back, {member?.first_name || 'Employee'}!</>}
          subtitle="Manage your health benefits, view claims, and access your employer-sponsored programs."
        >
          <div className="flex flex-wrap gap-3">
            <Link
              to="/employee-portal/benefits"
              className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Heart className="w-4 h-4 mr-2" />
              View Benefits
            </Link>
            <Link
              to="/employee-portal/member-services"
              className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Users className="w-4 h-4 mr-2" />
              Member Services
            </Link>
            <Link
              to="/employee-portal/incentives"
              className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Incentives & Cards
            </Link>
          </div>
        </PortalHero>

        {/* Benefits Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Out-of-Pocket Progress */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">OOP Progress</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                {accumulator?.plan_type === 'individual' ? 'Individual' : 'Family'}
              </span>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Deductible</span>
                  <span className="font-semibold">${accumulator?.deductible_met || 0} / ${accumulator?.deductible_total || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${deductiblePercent}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Out-of-Pocket Max</span>
                  <span className="font-semibold">${accumulator?.oop_met || 0} / ${accumulator?.oop_max || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all"
                    style={{ width: `${oopPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* HSA Balance */}
          {hsaAccount && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">HSA Balance</span>
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-700 mb-2">${hsaAccount.balance.toFixed(2)}</p>
              <div className="space-y-1 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>YTD Contributions</span>
                  <span className="font-medium">${hsaAccount.ytd_contributions}</span>
                </div>
                <div className="flex justify-between">
                  <span>Employer Contribution</span>
                  <span className="font-medium">${hsaAccount.employer_contributions}</span>
                </div>
                <div className="flex justify-between">
                  <span>Remaining Limit</span>
                  <span className="font-medium">${hsaAccount.annual_limit - hsaAccount.ytd_contributions}</span>
                </div>
              </div>
            </div>
          )}

          {/* FSA Balance */}
          {fsaAccount && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">FSA Balance</span>
                <DollarSign className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-700 mb-2">${fsaAccount.balance.toFixed(2)}</p>
              <div className="space-y-1 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Annual Election</span>
                  <span className="font-medium">${fsaAccount.ytd_contributions}</span>
                </div>
                <div className="flex justify-between">
                  <span>Spent</span>
                  <span className="font-medium">${fsaAccount.ytd_contributions - fsaAccount.balance}</span>
                </div>
                {fsaAccount.use_by_date && (
                  <div className="flex justify-between text-amber-600">
                    <span>Use by {new Date(fsaAccount.use_by_date).toLocaleDateString()}</span>
                    <AlertCircle className="w-3 h-3" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recent Claims */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Recent Claims</span>
              <FileText className="w-4 h-4 text-amber-600" />
            </div>
            <div className="space-y-2">
              {recentClaims.slice(0, 2).map((claim) => (
                <div key={claim.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs font-medium text-gray-900">{formatDateOnly(claim.service_date)}</p>
                    <p className="text-xs text-gray-500 truncate" style={{maxWidth: '120px'}}>{claim.service_type}</p>
                  </div>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                    ${claim.patient_responsibility}
                  </span>
                </div>
              ))}
              <Link
                to="/employee-portal/benefits?tab=claims"
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center pt-1"
              >
                View All Claims <ChevronRight className="w-3 h-3 ml-1" />
              </Link>
            </div>
          </div>
        </div>

        {/* Plan Coverage Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Your Coverage</h2>
            </div>
            <Link
              to="/employee-portal/benefits"
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
            >
              Manage Coverage <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Medical Plan */}
            {medicalPlan && (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Heart className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">Medical</h3>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Active
                  </span>
                </div>
                <p className="text-lg font-bold text-blue-800 mb-2">{medicalPlan.plan_name}</p>
                <div className="space-y-1 text-sm text-blue-700">
                  <div className="flex justify-between">
                    <span>Carrier:</span>
                    <span className="font-medium">{medicalPlan.carrier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Coverage:</span>
                    <span className="font-medium">{medicalPlan.coverage_tier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>PCP Copay:</span>
                    <span className="font-medium">${medicalPlan.copay_pcp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Specialist Copay:</span>
                    <span className="font-medium">${medicalPlan.copay_specialist}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Dental Plan */}
            {dentalPlan && (
              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Stethoscope className="w-5 h-5 text-teal-600" />
                    <h3 className="font-semibold text-teal-900">Dental</h3>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Active
                  </span>
                </div>
                <p className="text-lg font-bold text-teal-800 mb-2">{dentalPlan.plan_name}</p>
                <div className="space-y-1 text-sm text-teal-700">
                  <div className="flex justify-between">
                    <span>Carrier:</span>
                    <span className="font-medium">{dentalPlan.carrier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Coverage:</span>
                    <span className="font-medium">{dentalPlan.coverage_tier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Preventive:</span>
                    <span className="font-medium">100%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Basic:</span>
                    <span className="font-medium">80%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Vision Plan */}
            {visionPlan && (
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Eye className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-indigo-900">Vision</h3>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Active
                  </span>
                </div>
                <p className="text-lg font-bold text-indigo-800 mb-2">{visionPlan.plan_name}</p>
                <div className="space-y-1 text-sm text-indigo-700">
                  <div className="flex justify-between">
                    <span>Carrier:</span>
                    <span className="font-medium">{visionPlan.carrier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Coverage:</span>
                    <span className="font-medium">{visionPlan.coverage_tier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Eye Exam:</span>
                    <span className="font-medium">$10 copay</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Frames:</span>
                    <span className="font-medium">$150 allowance</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/employee-portal/benefits?tab=id-cards">
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                View ID Cards
              </button>
            </Link>
            <Link to="/employee-portal/benefits?tab=dependents">
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center">
                <Users className="w-4 h-4 mr-1" />
                Manage Dependents
              </button>
            </Link>
            <Link to="/employee-portal/benefits?tab=providers">
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center">
                <Building2 className="w-4 h-4 mr-1" />
                Find Providers
              </button>
            </Link>
          </div>
        </div>

        {/* Bottom Grid - Quick Access */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Member Services Card */}
          <Link to="/employee-portal/member-services">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Member Services</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Access referrals, program enrollments, and Trust services all in one place.
              </p>
              <div className="flex items-center text-purple-600 font-medium text-sm">
                View Services <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          </Link>

          {/* Incentives & Cards */}
          <Link to="/employee-portal/incentives">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Incentives & Cards</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                View your rewards, prepaid card balance, and payment history.
              </p>
              <div className="flex items-center text-green-600 font-medium text-sm">
                View Incentives <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          </Link>

          {/* Messages */}
          <Link to="/employee-portal/messages">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
                {unreadMessages > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    {unreadMessages} new
                  </span>
                )}
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Communicate with your benefits team and care coordinators.
              </p>
              <div className="flex items-center text-blue-600 font-medium text-sm">
                View Messages <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </div>
          </Link>
        </div>

        {/* Upcoming Deadlines */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Calendar className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-amber-900">Important Dates</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">Open Enrollment Ends</p>
                <p className="text-xs text-gray-600">December 15, 2025</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">FSA Deadline</p>
                <p className="text-xs text-gray-600">March 15, 2026</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Activity className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">Wellness Challenge</p>
                <p className="text-xs text-gray-600">Starts January 15, 2026</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </EmployeePortalLayout>
  );
}
